import styled from 'styled-components';
import Image from 'next/legacy/image';
import type { StaticImport } from 'next/dist/shared/lib/get-img-props';

import { media, gridBase, pxToRem } from '@yoweb/ui/styles/utils';
import { Title, Label, Text } from '@yoweb/ui/components/typography';
import { Container } from '@yoweb/ui/components/containers';
import { Box } from '@yoweb/ui/components/Box';

const HERO_IMAGE_RATIO = 392 / 234;

type FaqHeroProps = {
  label: string;
  title: string;
  subtitle: string;
  image: string | StaticImport;
} & DataTestId;

export const FaqHero = ({
  label,
  title,
  subtitle,
  image,
  'data-testid': dataTestId = 'faq-hero',
}: FaqHeroProps) => (
  <ContainerGrid data-testid={`${dataTestId}-container`}>
    <HeroTextArea data-testid={`${dataTestId}-textarea`}>
      <Label size="md">{label}</Label>
      <Box mt={14}>
        <Title data-testid={`${dataTestId}-title`} size={{ _: 'xl', lg: 'xxl' }}>
          {title}
        </Title>
      </Box>
      <HeroTextBox mt="normal2">
        <Text data-testid={`${dataTestId}-subtitle`} size="xl" weight="regular" variant="muted">
          {subtitle}
        </Text>
      </HeroTextBox>
    </HeroTextArea>
    <HeroImageArea data-testid={`${dataTestId}-image-area`}>
      <HeroImageRatio>
        <Image data-testid={`${dataTestId}-image`} src={image} width={392} height={234} priority />
      </HeroImageRatio>
    </HeroImageArea>
  </ContainerGrid>
);

const ContainerGrid = styled(Container)`
  && {
    ${gridBase};
  }
`;

const HeroTextArea = styled.div`
  grid-column: 1/5;
  ${media.md`
    grid-column: 1/5;
  `}
  ${media.lg`
    grid-column: 1/8;
  `}
`;

const HeroTextBox = styled(Box)`
  && {
    width: ${(5 / 6) * 100}%;
  }
`;

const HeroImageArea = styled.div`
  position: relative;
  grid-column: 1/5;
  display: grid;
  place-items: center center;
  margin-top: ${pxToRem(48)};
  ${media.md`
    margin-top: unset;
    grid-column: 5/9;
  `}
  ${media.lg`
    grid-column: 8/12;
  `}
`;

const HeroImageRatio = styled.div`
  width: ${pxToRem(234)};
  aspect-ratio: ${HERO_IMAGE_RATIO};
  ${media.md`
    width: 100%;
  `}
`;
